import React, { useState, useRef, useEffect } from "react";
import styles from "./styles";
import { todayEastern } from "../dateUtils";

// Main component for creating new handwritten notes
export const HandwrittenNoteModal = ({ isOpen, onClose, onSubmit, clientId, clientName }) => {
  const [date, setDate] = useState(todayEastern());
  const [drawingData, setDrawingData] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [title, setTitle] = useState("");
  
  // Tool state
  const [activeColor, setActiveColor] = useState("#000000");
  const [lineWidth, setLineWidth] = useState(2);
  const [activeTool, setActiveTool] = useState("pen"); // pen, eraser, highlighter
  const [lastX, setLastX] = useState(null);
  const [lastY, setLastY] = useState(null);
  
  // History for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Panel state
  const [toolbarExpanded, setToolbarExpanded] = useState(true);
  
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  
  // Available colors
  const colors = [
    { color: "#000000", name: "Black" },
    { color: "var(--primary-color)", name: "Dark Blue" },
    { color: "var(--error-color)", name: "Red" },
    { color: "var(--success-color)", name: "Green" },
    { color: "#2980b9", name: "Blue" },
    { color: "#8e44ad", name: "Purple" },
    { color: "#f39c12", name: "Orange" },
    { color: "#16a085", name: "Teal" }
  ];
  
  // Available line widths
  const lineWidths = [
    { size: 1, name: "Fine" },
    { size: 2, name: "Thin" },
    { size: 4, name: "Medium" },
    { size: 6, name: "Thick" },
    { size: 10, name: "Extra Thick" },
  ];
  
  // Initialize canvas on component mount
  useEffect(() => {
    if (isOpen) {
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = activeColor;
        ctxRef.current = ctx;
        
        // Add initial blank canvas to history
        addToHistory();
      }
    }
  }, [isOpen]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const imageData = canvas.toDataURL();
        
        // Store current dimensions
        const currentWidth = canvas.width;
        const currentHeight = canvas.height;
        
        // Resize canvas
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        
        // Restore context properties
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = activeColor;
        
        // Redraw image
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, currentWidth, currentHeight, 0, 0, canvas.width, canvas.height);
        };
        img.src = imageData;
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [lineWidth, activeColor]);
  
  // Update context when tool settings change
  useEffect(() => {
    if (ctxRef.current) {
      ctxRef.current.lineWidth = lineWidth;
      ctxRef.current.strokeStyle = activeTool === "eraser" ? "#ffffff" : 
                                    activeTool === "highlighter" ? `${activeColor}40` : activeColor;
      
      // For highlighter, use wider line and different compositing
      if (activeTool === "highlighter") {
        ctxRef.current.lineWidth = lineWidth * 2;
        ctxRef.current.globalAlpha = 0.3;
      } else if (activeTool === "eraser") {
        ctxRef.current.lineWidth = lineWidth * 2; // Larger eraser
        ctxRef.current.globalAlpha = 1;
      } else {
        ctxRef.current.globalAlpha = 1;
      }
    }
  }, [activeTool, activeColor, lineWidth]);
  
  // Handle modal overlay click - prevent closing when clicking inside modal content
  const handleOverlayClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };
  
  // Add current canvas state to history
  const addToHistory = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL();
    
    // If we're not at the end of history, remove future states
    if (historyIndex < history.length - 1) {
      setHistory(history.slice(0, historyIndex + 1));
    }
    
    setHistory(prev => [...prev, imageData]);
    setHistoryIndex(prev => prev + 1);
    setDrawingData(imageData);
  };
  
  // Undo last action
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    
    const imageData = history[newIndex];
    if (imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
        setDrawingData(imageData);
      };
      img.src = imageData;
    }
  };
  
  // Redo last undone action
  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    
    const imageData = history[newIndex];
    if (imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
        setDrawingData(imageData);
      };
      img.src = imageData;
    }
  };
  
  // Drawing functions
  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    const { offsetX, offsetY } = getCoordinates(e);
    
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setLastX(offsetX);
    setLastY(offsetY);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling
    
    const { offsetX, offsetY } = getCoordinates(e);
    
    // Different drawing behavior for different tools
    if (activeTool === "pen" || activeTool === "eraser") {
      ctxRef.current.lineTo(offsetX, offsetY);
      ctxRef.current.stroke();
    } else if (activeTool === "highlighter") {
      ctxRef.current.lineTo(offsetX, offsetY);
      ctxRef.current.stroke();
    }
    
    setLastX(offsetX);
    setLastY(offsetY);
  };
  
  const stopDrawing = () => {
    ctxRef.current.closePath();
    setIsDrawing(false);
    
    // Save this step in history after completing a stroke
    addToHistory();
  };
  
  // Get coordinates for both mouse and touch events
  const getCoordinates = (e) => {
    if (e.type.includes('touch')) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: touch.clientX - rect.left,
        offsetY: touch.clientY - rect.top
      };
    } else {
      return {
        offsetX: e.nativeEvent.offsetX,
        offsetY: e.nativeEvent.offsetY
      };
    }
  };
  
  // Clear canvas
  const clearCanvas = () => {
    if (window.confirm("Are you sure you want to clear the entire drawing?")) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawingData("");
      
      // Add cleared state to history
      addToHistory();
    }
  };
  
  // Handle color change
  const handleColorChange = (color) => {
    setActiveColor(color);
    
    // If using eraser, switch back to pen
    if (activeTool === "eraser") {
      setActiveTool("pen");
    }
  };
  
  // Handle line width change
  const handleLineWidthChange = (width) => {
    setLineWidth(width);
  };
  
  // Handle tool change
  const handleToolChange = (tool) => {
    setActiveTool(tool);
  };
  
  // Toggle toolbar expansion
  const toggleToolbar = () => {
    setToolbarExpanded(!toolbarExpanded);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const noteData = {
      type: "HandwrittenNote",
      date,
      title,
      drawingData,
      clientId,
      clientName,
      created_at: new Date().toISOString()
    };
    
    onSubmit(noteData);
  };
  
  // Download the drawing as PNG
  const handleDownload = () => {
    if (!drawingData) return;
    
    const link = document.createElement('a');
    link.download = `${title || 'handwritten-note'}-${date}.png`;
    link.href = drawingData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={handleOverlayClick} style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>New Handwritten Note</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          {/* Header with date and title inputs */}
          <div style={modalStyles.headerControls}>
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>
                Date:
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={modalStyles.input}
                  required
                />
              </label>
            </div>
            
            <div style={modalStyles.formGroup}>
              <label style={modalStyles.label}>
                Title:
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  style={modalStyles.input}
                  placeholder="Enter a title for this note"
                  required
                />
              </label>
            </div>
          </div>
          
          {/* Main drawing area with collapsible toolbar */}
          <div style={modalStyles.drawingContainer}>
            {/* Canvas Wrapper */}
            <div style={modalStyles.canvasWrapper}>
              <div style={modalStyles.canvasContainer}>
                <canvas
                  ref={canvasRef}
                  style={modalStyles.canvas}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                />
              </div>
            </div>
            
            {/* Toolbar Toggle Button */}
            <button 
              type="button"
              onClick={toggleToolbar}
              style={toolbarExpanded ? modalStyles.hideToolbarButton : modalStyles.showToolbarButton}
              title={toolbarExpanded ? "Hide Toolbar" : "Show Toolbar"}
            >
              {toolbarExpanded ? "→" : "←"}
            </button>
            
            {/* Collapsible Toolbar */}
            <div style={{
              ...modalStyles.toolsPanel,
              display: toolbarExpanded ? 'flex' : 'none',
            }}>
              {/* Quick Tools */}
              <div style={modalStyles.toolSection}>
                <h4 style={modalStyles.toolSectionTitle}>Tools</h4>
                <div style={modalStyles.toolButtons}>
                  <button
                    type="button"
                    onClick={() => handleToolChange("pen")}
                    style={{
                      ...modalStyles.toolButton,
                      backgroundColor: activeTool === "pen" ? "var(--border-color)" : "transparent",
                    }}
                    title="Pen"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToolChange("highlighter")}
                    style={{
                      ...modalStyles.toolButton,
                      backgroundColor: activeTool === "highlighter" ? "var(--border-color)" : "transparent",
                    }}
                    title="Highlighter"
                  >
                    🖌️
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToolChange("eraser")}
                    style={{
                      ...modalStyles.toolButton,
                      backgroundColor: activeTool === "eraser" ? "var(--border-color)" : "transparent",
                    }}
                    title="Eraser"
                  >
                    🧽
                  </button>
                </div>
              </div>
              
              {/* Line Width */}
              <div style={modalStyles.toolSection}>
                <h4 style={modalStyles.toolSectionTitle}>Line Width</h4>
                <div style={modalStyles.lineWidthButtons}>
                  {lineWidths.map((item) => (
                    <button
                      key={item.size}
                      type="button"
                      onClick={() => handleLineWidthChange(item.size)}
                      style={{
                        ...modalStyles.lineWidthButton,
                        backgroundColor: lineWidth === item.size ? "var(--border-color)" : "transparent",
                      }}
                      title={item.name}
                    >
                      <div 
                        style={{
                          ...modalStyles.lineWidthPreview,
                          height: `${item.size}px`,
                        }}
                      ></div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Color Picker */}
              <div style={modalStyles.toolSection}>
                <h4 style={modalStyles.toolSectionTitle}>Colors</h4>
                <div style={modalStyles.colorButtons}>
                  {colors.map((item) => (
                    <button
                      key={item.color}
                      type="button"
                      onClick={() => handleColorChange(item.color)}
                      style={{
                        ...modalStyles.colorButton,
                        backgroundColor: item.color,
                        boxShadow: activeColor === item.color ? "0 0 0 2px #4b5563" : "none",
                      }}
                      title={item.name}
                    ></button>
                  ))}
                </div>
              </div>
              
              {/* History Controls */}
              <div style={modalStyles.toolSection}>
                <h4 style={modalStyles.toolSectionTitle}>Actions</h4>
                <div style={modalStyles.actionButtons}>
                  <button
                    type="button"
                    onClick={handleUndo}
                    style={{
                      ...modalStyles.actionButton,
                      opacity: historyIndex <= 0 ? 0.5 : 1,
                    }}
                    disabled={historyIndex <= 0}
                    title="Undo"
                  >
                    ↩️ Undo
                  </button>
                  <button
                    type="button"
                    onClick={handleRedo}
                    style={{
                      ...modalStyles.actionButton,
                      opacity: historyIndex >= history.length - 1 ? 0.5 : 1,
                    }}
                    disabled={historyIndex >= history.length - 1}
                    title="Redo"
                  >
                    ↪️ Redo
                  </button>
                  <button
                    type="button"
                    onClick={clearCanvas}
                    style={modalStyles.actionButton}
                    title="Clear All"
                  >
                    🗑️ Clear All
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    style={{
                      ...modalStyles.actionButton,
                      opacity: !drawingData ? 0.5 : 1,
                    }}
                    disabled={!drawingData}
                    title="Download"
                  >
                    💾 Save PNG
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div style={modalStyles.buttonGroup}>
            <button type="button" onClick={onClose} style={modalStyles.cancelButton}>
              Cancel
            </button>
            <button 
              type="submit" 
              style={modalStyles.submitButton}
              disabled={!drawingData}
            >
              Save Note
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Component for viewing/editing existing handwritten notes
export const HandwrittenNoteViewModal = ({ isOpen, note, onClose, onUpdate, onSendAsPDF }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [date, setDate] = useState("");
  const [title, setTitle] = useState("");
  const [drawingData, setDrawingData] = useState("");
  
  // Tool state for editing mode
  const [activeColor, setActiveColor] = useState("#000000");
  const [lineWidth, setLineWidth] = useState(2);
  const [activeTool, setActiveTool] = useState("pen"); // pen, eraser, highlighter
  const [isDrawing, setIsDrawing] = useState(false);
  const [lastX, setLastX] = useState(null);
  const [lastY, setLastY] = useState(null);
  
  // History for undo/redo in edit mode
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  // Panel state
  const [toolbarExpanded, setToolbarExpanded] = useState(true);
  
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  
  // Available colors
  const colors = [
    { color: "#000000", name: "Black" },
    { color: "var(--primary-color)", name: "Dark Blue" },
    { color: "var(--error-color)", name: "Red" },
    { color: "var(--success-color)", name: "Green" },
    { color: "#2980b9", name: "Blue" },
    { color: "#8e44ad", name: "Purple" },
    { color: "#f39c12", name: "Orange" },
    { color: "#16a085", name: "Teal" }
  ];
  
  // Available line widths
  const lineWidths = [
    { size: 1, name: "Fine" },
    { size: 2, name: "Thin" },
    { size: 4, name: "Medium" },
    { size: 6, name: "Thick" },
    { size: 10, name: "Extra Thick" },
  ];
  
  // Parse note data on component mount
  useEffect(() => {
    if (note && note.notes) {
      try {
        const parsedNote = JSON.parse(note.notes);
        setDate(parsedNote.date || "");
        setTitle(parsedNote.title || "");
        setDrawingData(parsedNote.drawingData || "");
      } catch (err) {
        console.error("Error parsing note data:", err);
      }
    }
  }, [note]);
  
  // Initialize canvas when in editing mode
  useEffect(() => {
    if (isEditing && canvasRef.current) {
      const canvas = canvasRef.current;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      const ctx = canvas.getContext("2d");
      ctx.lineWidth = lineWidth;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = activeColor;
      ctxRef.current = ctx;
      
      // Load existing drawing if available
      if (drawingData) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          // Initialize history with loaded image
          setHistory([drawingData]);
          setHistoryIndex(0);
        };
        img.src = drawingData;
      }
    }
  }, [isEditing, drawingData]);
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && isEditing) {
        const canvas = canvasRef.current;
        const imageData = canvas.toDataURL();
        
        // Store current dimensions
        const currentWidth = canvas.width;
        const currentHeight = canvas.height;
        
        // Resize canvas
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        
        // Restore context properties
        const ctx = canvas.getContext("2d");
        ctx.lineWidth = lineWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = activeColor;
        
        // Redraw image
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, currentWidth, currentHeight, 0, 0, canvas.width, canvas.height);
        };
        img.src = imageData;
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [lineWidth, activeColor, isEditing]);
  
  // Update context when tool settings change
  useEffect(() => {
    if (ctxRef.current) {
      ctxRef.current.lineWidth = lineWidth;
      ctxRef.current.strokeStyle = activeTool === "eraser" ? "#ffffff" : 
                                    activeTool === "highlighter" ? `${activeColor}40` : activeColor;
      
      // For highlighter, use wider line and different compositing
      if (activeTool === "highlighter") {
        ctxRef.current.lineWidth = lineWidth * 2;
        ctxRef.current.globalAlpha = 0.3;
      } else if (activeTool === "eraser") {
        ctxRef.current.lineWidth = lineWidth * 2; // Larger eraser
        ctxRef.current.globalAlpha = 1;
      } else {
        ctxRef.current.globalAlpha = 1;
      }
    }
  }, [activeTool, activeColor, lineWidth]);
  
  // Handle modal overlay click
  const handleOverlayClick = (e) => {
    if (e.target.className === "modal-overlay") {
      onClose();
    }
  };
  
  // Add current canvas state to history
  const addToHistory = () => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL();
    
    // If we're not at the end of history, remove future states
    if (historyIndex < history.length - 1) {
      setHistory(history.slice(0, historyIndex + 1));
    }
    
    setHistory(prev => [...prev, imageData]);
    setHistoryIndex(prev => prev + 1);
    setDrawingData(imageData);
  };
  
  // Undo last action
  const handleUndo = () => {
    if (historyIndex <= 0) return;
    
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    
    const imageData = history[newIndex];
    if (imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
        setDrawingData(imageData);
      };
      img.src = imageData;
    }
  };
  
  // Redo last undone action
  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;
    
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    
    const imageData = history[newIndex];
    if (imageData && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(img, 0, 0);
        setDrawingData(imageData);
      };
      img.src = imageData;
    }
  };
  
  // Drawing functions
  const startDrawing = (e) => {
    e.preventDefault(); // Prevent scrolling on touch devices
    const { offsetX, offsetY } = getCoordinates(e);
    
    ctxRef.current.beginPath();
    ctxRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
    setLastX(offsetX);
    setLastY(offsetY);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault(); // Prevent scrolling
    
    const { offsetX, offsetY } = getCoordinates(e);
    
    // Different drawing behavior for different tools
    if (activeTool === "pen" || activeTool === "eraser") {
      ctxRef.current.lineTo(offsetX, offsetY);
      ctxRef.current.stroke();
    } else if (activeTool === "highlighter") {
      ctxRef.current.lineTo(offsetX, offsetY);
      ctxRef.current.stroke();
    }
    
    setLastX(offsetX);
    setLastY(offsetY);
  };
  
  const stopDrawing = () => {
    ctxRef.current.closePath();
    setIsDrawing(false);
    
    // Save this step in history after completing a stroke
    addToHistory();
  };
  
  // Get coordinates for both mouse and touch events
  const getCoordinates = (e) => {
    if (e.type.includes('touch')) {
      const touch = e.touches[0];
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: touch.clientX - rect.left,
        offsetY: touch.clientY - rect.top
      };
    } else {
      return {
        offsetX: e.nativeEvent.offsetX,
        offsetY: e.nativeEvent.offsetY
      };
    }
  };
  
  // Clear canvas
  const clearCanvas = () => {
    if (window.confirm("Are you sure you want to clear the entire drawing?")) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDrawingData("");
      
      // Add cleared state to history
      addToHistory();
    }
  };
  
  // Handle color change
  const handleColorChange = (color) => {
    setActiveColor(color);
    
    // If using eraser, switch back to pen
    if (activeTool === "eraser") {
      setActiveTool("pen");
    }
  };
  
  // Handle line width change
  const handleLineWidthChange = (width) => {
    setLineWidth(width);
  };
  
  // Handle tool change
  const handleToolChange = (tool) => {
    setActiveTool(tool);
  };
  
  // Toggle toolbar expansion
  const toggleToolbar = () => {
    setToolbarExpanded(!toolbarExpanded);
  };
  
  // Enable editing mode
  const handleEdit = () => {
    setIsEditing(true);
    setToolbarExpanded(true); // Ensure toolbar is visible when editing starts
  };
  
  // Save changes
  const handleSave = () => {
    const updatedNote = {
      type: "HandwrittenNote",
      date,
      title,
      drawingData,
      updated_at: new Date().toISOString()
    };
    
    onUpdate(note.id, updatedNote);
  };
  
  // Download the drawing as PNG
  const handleDownload = () => {
    if (!drawingData) return;
    
    const link = document.createElement('a');
    link.download = `${title || 'handwritten-note'}-${date}.png`;
    link.href = drawingData;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={handleOverlayClick} style={modalStyles.overlay}>
      <div style={modalStyles.modal}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>
            {isEditing ? "Edit Handwritten Note" : "View Handwritten Note"}
          </h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <div style={modalStyles.content}>
          {isEditing ? (
            <>
              {/* Header with date and title inputs */}
              <div style={modalStyles.headerControls}>
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>
                    Date:
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      style={modalStyles.input}
                      required
                    />
                  </label>
                </div>
                
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>
                    Title:
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      style={modalStyles.input}
                      required
                    />
                  </label>
                </div>
              </div>
              
              {/* Main drawing area with collapsible toolbar */}
              <div style={modalStyles.drawingContainer}>
                {/* Canvas Wrapper */}
                <div style={modalStyles.canvasWrapper}>
                  <div style={modalStyles.canvasContainer}>
                    <canvas
                      ref={canvasRef}
                      style={modalStyles.canvas}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                </div>
                
                {/* Toolbar Toggle Button */}
                <button 
                  type="button"
                  onClick={toggleToolbar}
                  style={toolbarExpanded ? modalStyles.hideToolbarButton : modalStyles.showToolbarButton}
                  title={toolbarExpanded ? "Hide Toolbar" : "Show Toolbar"}
                >
                  {toolbarExpanded ? "→" : "←"}
                </button>
                
                {/* Collapsible Toolbar */}
                <div style={{
                  ...modalStyles.toolsPanel,
                  display: toolbarExpanded ? 'flex' : 'none',
                }}>
                  {/* Drawing Tools */}
                  <div style={modalStyles.toolSection}>
                    <h4 style={modalStyles.toolSectionTitle}>Tools</h4>
                    <div style={modalStyles.toolButtons}>
                      <button
                        type="button"
                        onClick={() => handleToolChange("pen")}
                        style={{
                          ...modalStyles.toolButton,
                          backgroundColor: activeTool === "pen" ? "var(--border-color)" : "transparent",
                        }}
                        title="Pen"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToolChange("highlighter")}
                        style={{
                          ...modalStyles.toolButton,
                          backgroundColor: activeTool === "highlighter" ? "var(--border-color)" : "transparent",
                        }}
                        title="Highlighter"
                      >
                        🖌️
                      </button>
                      <button
                        type="button"
                        onClick={() => handleToolChange("eraser")}
                        style={{
                          ...modalStyles.toolButton,
                          backgroundColor: activeTool === "eraser" ? "var(--border-color)" : "transparent",
                        }}
                        title="Eraser"
                      >
                        🧽
                      </button>
                    </div>
                  </div>
                  
                  {/* Line Width */}
                  <div style={modalStyles.toolSection}>
                    <h4 style={modalStyles.toolSectionTitle}>Line Width</h4>
                    <div style={modalStyles.lineWidthButtons}>
                      {lineWidths.map((item) => (
                        <button
                          key={item.size}
                          type="button"
                          onClick={() => handleLineWidthChange(item.size)}
                          style={{
                            ...modalStyles.lineWidthButton,
                            backgroundColor: lineWidth === item.size ? "var(--border-color)" : "transparent",
                          }}
                          title={item.name}
                        >
                          <div 
                            style={{
                              ...modalStyles.lineWidthPreview,
                              height: `${item.size}px`,
                            }}
                          ></div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Color Picker */}
                  <div style={modalStyles.toolSection}>
                    <h4 style={modalStyles.toolSectionTitle}>Colors</h4>
                    <div style={modalStyles.colorButtons}>
                      {colors.map((item) => (
                        <button
                          key={item.color}
                          type="button"
                          onClick={() => handleColorChange(item.color)}
                          style={{
                            ...modalStyles.colorButton,
                            backgroundColor: item.color,
                            boxShadow: activeColor === item.color ? "0 0 0 2px #4b5563" : "none",
                          }}
                          title={item.name}
                        ></button>
                      ))}
                    </div>
                  </div>
                  
                  {/* History Controls */}
                  <div style={modalStyles.toolSection}>
                    <h4 style={modalStyles.toolSectionTitle}>Actions</h4>
                    <div style={modalStyles.actionButtons}>
                      <button
                        type="button"
                        onClick={handleUndo}
                        style={{
                          ...modalStyles.actionButton,
                          opacity: historyIndex <= 0 ? 0.5 : 1,
                        }}
                        disabled={historyIndex <= 0}
                        title="Undo"
                      >
                        ↩️ Undo
                      </button>
                      <button
                        type="button"
                        onClick={handleRedo}
                        style={{
                          ...modalStyles.actionButton,
                          opacity: historyIndex >= history.length - 1 ? 0.5 : 1,
                        }}
                        disabled={historyIndex >= history.length - 1}
                        title="Redo"
                      >
                        ↪️ Redo
                      </button>
                      <button
                        type="button"
                        onClick={clearCanvas}
                        style={modalStyles.actionButton}
                        title="Clear All"
                      >
                        🗑️ Clear All
                      </button>
                      <button
                        type="button"
                        onClick={handleDownload}
                        style={{
                          ...modalStyles.actionButton,
                          opacity: !drawingData ? 0.5 : 1,
                        }}
                        disabled={!drawingData}
                        title="Download"
                      >
                        💾 Save PNG
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={modalStyles.viewInfo}>
                <div style={modalStyles.viewField}>
                  <strong>Date:</strong> {date}
                </div>
                <div style={modalStyles.viewField}>
                  <strong>Title:</strong> {title}
                </div>
              </div>
              
              <div style={modalStyles.viewDrawingContainer}>
                <div style={modalStyles.viewCanvasWrapper}>
                  <div style={modalStyles.imageContainer}>
                    {drawingData ? (
                      <img 
                        src={drawingData} 
                        alt="Handwritten note" 
                        style={modalStyles.noteImage} 
                      />
                    ) : (
                      <p style={modalStyles.noImageText}>No drawing available</p>
                    )}
                  </div>
                </div>
                
                <div style={modalStyles.viewToolbar}>
                  <button 
                    type="button" 
                    onClick={handleDownload}
                    style={{
                      ...modalStyles.actionButton,
                      opacity: !drawingData ? 0.5 : 1,
                    }}
                    disabled={!drawingData}
                  >
                    💾 Download as PNG
                  </button>
                </div>
              </div>
            </>
          )}
          
          <div style={modalStyles.buttonGroup}>
            {isEditing ? (
              <>
                <button type="button" onClick={() => setIsEditing(false)} style={modalStyles.cancelButton}>
                  Cancel
                </button>
                <button type="button" onClick={handleSave} style={modalStyles.submitButton}>
                  Save Changes
                </button>
              </>
            ) : (
              <>
                {onSendAsPDF && (
                  <button type="button" onClick={() => onSendAsPDF(note)} style={{ ...modalStyles.cancelButton, backgroundColor: "var(--primary-color)", color: "#fff", borderColor: "var(--primary-color)" }}>
                    ✉️ Send as PDF
                  </button>
                )}
                <button type="button" onClick={onClose} style={modalStyles.cancelButton}>
                  Close
                </button>
                <button type="button" onClick={handleEdit} style={modalStyles.editButton}>
                  Edit Note
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Modal styles
const modalStyles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "95%",
    maxWidth: "1200px",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 20px",
    borderBottom: "1px solid var(--border-color)",
    backgroundColor: "var(--surface-muted)"
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: 600,
    color: "#343a40"
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "var(--text-muted)"
  },
  form: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    height: "calc(90vh - 70px)" // Subtract header height
  },
  content: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    height: "calc(90vh - 70px)" // Subtract header height
  },
  formGroup: {
    marginBottom: "15px"
  },
  headerControls: {
    display: "flex",
    gap: "20px",
    marginBottom: "15px"
  },
  drawingContainer: {
    display: "flex",
    flex: 1,
    minHeight: 0, // Necessary for proper flex sizing
    position: "relative" // For overlay toolbar positioning
  },
  viewDrawingContainer: {
    display: "flex",
    flex: 1,
    position: "relative",
    maxHeight: "calc(90vh - 180px)" // Allow space for buttons and info
  },
  canvasWrapper: {
    flex: 1,
    minWidth: 0, // Necessary for proper flex sizing
    display: "flex"
  },
  viewCanvasWrapper: {
    flex: 1,
    minWidth: 0,
    display: "flex"
  },
  canvasContainer: {
    border: "1px solid #ced4da",
    borderRadius: "4px",
    overflow: "hidden",
    flex: 1,
    display: "flex"
  },
  canvas: {
    width: "100%",
    height: "100%",
    backgroundColor: "#fff",
    cursor: "crosshair",
    touchAction: "none" // Prevents scrolling while drawing on touch devices
  },
  // Collapsible Toolbar Styles
  toolsPanel: {
    backgroundColor: "var(--surface-muted)",
    padding: "15px",
    borderRadius: "8px 0 0 8px",
    border: "1px solid var(--border-color)",
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    width: "250px",
    minWidth: "250px", // Prevent shrinking
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.1)",
    zIndex: 10,
    transition: "transform 0.3s ease"
  },
  hideToolbarButton: {
    position: "absolute",
    right: "250px",
    top: "10px",
    zIndex: 20,
    width: "30px",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "var(--surface-muted)",
    border: "1px solid var(--border-color)",
    borderRadius: "4px 0 0 4px",
    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    transition: "all 0.3s ease"
  },
  showToolbarButton: {
    position: "absolute",
    right: "0px",
    top: "10px",
    zIndex: 20,
    width: "30px",
    height: "30px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "var(--surface-muted)",
    border: "1px solid var(--border-color)",
    borderRadius: "4px 0 0 4px",
    boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    transition: "all 0.3s ease"
  },
  viewToolbar: {
    position: "absolute",
    top: "10px",
    right: "10px",
    backgroundColor: "var(--surface-muted)",
    padding: "8px",
    borderRadius: "4px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    zIndex: 10
  },
  toolSection: {
    marginBottom: "10px"
  },
  toolSectionTitle: {
    fontSize: "14px",
    fontWeight: "600",
    margin: "0 0 8px 0",
    color: "#495057"
  },
  toolButtons: {
    display: "flex",
    gap: "8px"
  },
  toolButton: {
    width: "40px",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    background: "transparent",
    cursor: "pointer",
    fontSize: "18px"
  },
  lineWidthButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  lineWidthButton: {
    display: "flex",
    alignItems: "center",
    padding: "8px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    background: "transparent",
    cursor: "pointer"
  },
  lineWidthPreview: {
    width: "100%",
    backgroundColor: "#000",
    borderRadius: "4px"
  },
  colorButtons: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px"
  },
  colorButton: {
    width: "30px",
    height: "30px",
    borderRadius: "50%",
    border: "1px solid #ced4da",
    cursor: "pointer"
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  actionButton: {
    padding: "8px",
    backgroundColor: "var(--surface-muted)",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "5px"
  },
  buttonGroup: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    marginTop: "15px"
  },
  cancelButton: {
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    cursor: "pointer"
  },
  submitButton: {
    backgroundColor: "var(--primary-color)",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    cursor: "pointer"
  },
  editButton: {
    backgroundColor: "var(--success-color)",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    cursor: "pointer"
  },
  viewField: {
    marginBottom: "12px",
    fontSize: "14px"
  },
  viewInfo: {
    display: "flex",
    gap: "20px",
    marginBottom: "15px"
  },
  imageContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    overflow: "auto",
    backgroundColor: "#fff",
    flex: 1,
    height: "100%"
  },
  noteImage: {
    maxWidth: "100%",
    maxHeight: "100%"
  },
  noImageText: {
    color: "var(--text-muted)",
    fontStyle: "italic"
  },
  downloadButtonContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: "15px"
  },
  downloadButton: {
    backgroundColor: "var(--text-muted)",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 16px",
    fontSize: "14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "5px"
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: 500,
    color: "#495057"
  },
  input: {
    width: "100%",
    padding: "8px 12px",
    fontSize: "14px",
    border: "1px solid #ced4da",
    borderRadius: "4px",
    marginTop: "4px"
  }
};